import React, { useEffect, useRef, useState } from 'react';
import { Star, Lock, Sword, Shield, Sparkles, Gift } from 'lucide-react';
import gsap from 'gsap';
import { getProgressData, onEnterNode } from '../logic/progressLogic';
import { ElementInfoModal } from './ElementInfoModal';
import { ASSETS } from '../assetManifest';
import { ELEMENTS } from '../data';
import iconMap from '../iconMap';

const NodeDetailsModal = ({ node, onClose, onEnter }) => {
  if (!node) return null;

  const getNodeIcon = () => {
    const iconProps = { size: 24, className: 'text-white' };
    switch (node.type) {
      case 'boss':
        return <Sword {...iconProps} className="text-red-200" />;
      case 'story':
        return <Sparkles {...iconProps} className="text-blue-200" />;
      case 'challenge':
        return <Shield {...iconProps} className="text-yellow-200" />;
      case 'reward':
        return <Gift {...iconProps} className="text-green-200" />;
      default:
        return <Star {...iconProps} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50">
      <div className="bg-[#222050] rounded-lg p-6 max-w-lg w-full mx-4 border border-white">
        <div className="flex items-center gap-4 mb-4">
          {getNodeIcon()}
          <h2 className="text-xl font-bold text-amber-300">{node.name}</h2>
        </div>
        {node.level && (
          <div className="mb-2 text-amber-300 font-bold">Level {node.level}</div>
        )}
        {node.description && <p className="text-white mb-4">{node.description}</p>}
        {node.elements && (
          <div className="flex gap-2 mb-4">
            {node.elements.map((element, index) => (
              <div
                key={index}
                className={`p-2 rounded-full ${ELEMENTS[element].color}`}
              >
                {React.createElement(iconMap[element], {
                  size: 20,
                  fill: 'currentColor',
                })}
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            style={{
              backgroundImage: 'linear-gradient(180deg, #E8E8E8 0%, #4B4B4B 100%)',
              borderRadius: '12px',
            }}
            className="px-4 py-2 bg-gray-600 rounded hover:bg-gray-700 text-white"
          >
            Close
          </button>
          <button
            onClick={onEnter}
            style={{
              backgroundImage: 'linear-gradient(180deg, #FFB787 0%, #FFD24F 100%)',
              borderRadius: '12px',
            }}
            className="px-4 py-2 text-black"
          >
            Enter
          </button>
        </div>
      </div>
    </div>
  );
};

const ProgressNode = ({ node, position, onNodeClick }) => {
  const nodeRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (node.justUnlocked && nodeRef.current) {
      gsap.fromTo(
        nodeRef.current,
        { filter: 'brightness(10)' },
        {
          filter: 'brightness(1)',
          duration: 1,
          delay: 0.5,
          onComplete: () => {
            gsap.to(nodeRef.current, {
              boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
              duration: 0.5,
              yoyo: true,
              repeat: 2,
            });
          },
        }
      );
    }

    if (node.fadeOut && overlayRef.current) {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 1, backgroundColor: 'white' },
        { opacity: 0, duration: 1, ease: 'power2.out' }
      );
    }
  }, [node.justUnlocked, node.fadeOut]);

  const getNodeIcon = () => {
    const iconProps = { size: 24, className: 'text-white' };
    switch (node.type) {
      case 'boss':
        return <Sword {...iconProps} />;
      case 'story':
        return <Sparkles {...iconProps} />;
      case 'challenge':
        return <Shield {...iconProps} />;
      case 'reward':
        return <Gift {...iconProps} />;
      default:
        return <Star {...iconProps} />;
    }
  };

  const buttonClasses =
    node.completed || node.active
      ? 'bg-gradient-to-b from-orange-200 to-amber-300 border-2 border-violet-500 shadow-lg hover:scale-105 cursor-pointer'
      : 'bg-gradient-to-b from-stone-200 to-zinc-500 border-2 border-gray-300 opacity-90 cursor-not-allowed';

  return (
    <div
      ref={nodeRef}
      style={{
        position: 'absolute',
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: 2,
      }}
    >
      <button
        onClick={() => onNodeClick(node)}
        className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold relative text-white ${buttonClasses} transition-transform duration-300 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]`}
        data-active={node.active ? 'true' : 'false'}
      >
        {node.completed || node.active ? (
          getNodeIcon()
        ) : (
          <Lock size={24} className="text-gray-300" />
        )}
      </button>
    </div>
  );
};

function Progress({ app }) {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);

  const progressData = getProgressData();

  useEffect(() => {
    if (containerRef.current) {
      const updateSize = () => {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      };
      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
  }, []);

  useEffect(() => {
    const tl = gsap.timeline();
    tl.from('.progress-node', {
      scale: 0,
      opacity: 0,
      duration: 0.2,
      stagger: 0.05,
      ease: 'back.out(1.7)',
    }).to(
      '.progress-connection',
      { opacity: 1, duration: 0.15, stagger: 0.05 },
      '-=0.2'
    );
  }, [app, selectedNode]);

  const getNodePosition = (index) => {
    const verticalProgress = index / (progressData.nodes.length - 1);
    const amplitude = 30;
    const frequency = 3;
    const verticalSpacing = 75;
    return {
      x:
        50 +
        amplitude *
          Math.sin(
            2 * Math.PI * frequency * verticalProgress + Math.PI / 2
          ),
      y: 90 - verticalProgress * verticalSpacing,
    };
  };

  const getPathPoints = (startIndex, endIndex) => {
    const points = [];
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      const progress = startIndex + (endIndex - startIndex) * (i / steps);
      const pos = getNodePosition(progress);
      points.push(pos);
    }
    return points;
  };

  const renderPathDots = () => {
    return progressData.nodes.slice(0, -1).map((node, index) => {
      const isUnlocked = node.completed;
      const pathPoints = getPathPoints(index, index + 1);

      return (
        <div key={`path-${index}`}>
          {pathPoints.map((point, dotIndex) => {
            // Dots are #FFCA5F for all.
            const dotColor = '#FFCA5F';
            return (
              <div
                key={`dot-${index}-${dotIndex}`}
                style={{
                  position: 'absolute',
                  left: `${point.x}%`,
                  top: `${point.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: dotColor,
                  opacity: isUnlocked ? 1 : 0.6,
                  zIndex: 1,
                  boxShadow: isUnlocked
                    ? '0 0 2px rgba(0,0,0,0.2)'
                    : 'none',
                }}
              />
            );
          })}
        </div>
      );
    });
  };

  const handleNodeClick = (node) => {
    if (!node.completed && !node.active) return;
    setSelectedNode(node);
  };

  const handleNodeEnter = () => {
    if (selectedNode) {
      onEnterNode(app, selectedNode);
    }
  };

  return (
    <div
      ref={containerRef}
      className="h-full w-full relative bg-gradient-to-b from-violet-500 to-sky-400"
    >
      {containerSize.width > 0 && renderPathDots()}

      {progressData.nodes.map((node, index) => (
        <ProgressNode
          key={node.id}
          node={node}
          position={getNodePosition(index)}
          onNodeClick={handleNodeClick}
        />
      ))}

      {selectedNode && (
        <NodeDetailsModal
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
          onEnter={handleNodeEnter}
        />
      )}

      {selectedElement && (
        <ElementInfoModal
          element={selectedElement}
          onClose={() => setSelectedElement(null)}
        />
      )}
    </div>
  );
}

export default Progress;